import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
interface DimensionLabelProps {
  x: number;
  y: number;
  value: number;
  label: string;
  color: string;
  unit?: string;
  onUpdate: (value: number) => void;
  onClick: (e: React.MouseEvent) => void;
  isEditing: boolean;
  onEditComplete: () => void;
}
export function DimensionLabel({
  x,
  y,
  value,
  label,
  color,
  unit = 'cm',
  onUpdate,
  onClick,
  isEditing,
  onEditComplete
}: DimensionLabelProps) {
  const [inputValue, setInputValue] = useState(Math.round(value).toString());
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);
  useEffect(() => {
    setInputValue(Math.round(value).toString());
  }, [value]);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const newValue = parseFloat(inputValue);
      if (!isNaN(newValue) && newValue > 0) {
        onUpdate(newValue);
      }
      onEditComplete();
    } else if (e.key === 'Escape') {
      setInputValue(Math.round(value).toString());
      onEditComplete();
    }
  };
  if (isEditing) {
    return <foreignObject x={x - 90} y={y - 60} width={180} height={120} data-id="element-363">
        <div className="flex flex-col items-center gap-2" xmlns="http://www.w3.org/1999/xhtml" data-id="element-364">
          <div className="text-sm font-semibold" style={{
          color: '#FFFFFF'
        }} data-id="element-365">
            {label}
          </div>
          <input ref={inputRef} type="number" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={handleKeyDown} onBlur={onEditComplete} className="w-40 px-4 py-3 bg-[#2D2D2D] rounded-lg text-white text-center font-bold focus:outline-none focus:ring-2 pointer-events-auto border-2" style={{
          fontSize: '28px',
          borderColor: '#4FC3F7',
          boxShadow: `0 0 0 2px rgba(79, 195, 247, 0.25)`
        }} data-id="element-366" />
        </div>
      </foreignObject>;
  }
  return <motion.g initial={{
    opacity: 0,
    scale: 0.9
  }} animate={{
    opacity: 1,
    scale: 1
  }} onClick={onClick} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} className="cursor-pointer" data-id="element-367">
      {/* Dimension line with tick marks */}
      <g data-id="element-368">
        {/* Main dimension line */}
        <line x1={x - 70} y1={y} x2={x + 70} y2={y} stroke="#4FC3F7" strokeWidth={1.5} className="pointer-events-none" data-id="element-369" />

        {/* Left tick mark */}
        <line x1={x - 70} y1={y - 8} x2={x - 70} y2={y + 8} stroke="#4FC3F7" strokeWidth={2} className="pointer-events-none" data-id="element-370" />

        {/* Right tick mark */}
        <line x1={x + 70} y1={y - 8} x2={x + 70} y2={y + 8} stroke="#4FC3F7" strokeWidth={2} className="pointer-events-none" data-id="element-371" />
      </g>

      {/* Background for text */}
      <motion.rect x={x - 60} y={y - 32} width={120} height={54} fill={isHovered ? '#3A3A3A' : '#2D2D2D'} stroke="#4FC3F7" strokeWidth={2} rx={4} opacity={0.95} animate={{
      fill: isHovered ? '#3A3A3A' : '#2D2D2D'
    }} transition={{
      duration: 0.2
    }} data-id="element-372" />

      {/* Label text */}
      <text x={x} y={y - 12} fill="#4FC3F7" fontSize={12} fontWeight="600" fontFamily="Inter, sans-serif" textAnchor="middle" className="pointer-events-none select-none" data-id="element-373">
        {label}
      </text>

      {/* Value text - LARGER */}
      <text x={x} y={y + 12} fill="#FFFFFF" fontSize={22} fontWeight="900" fontFamily="Inter, sans-serif" textAnchor="middle" className="pointer-events-none select-none" data-id="element-374">
        {Math.round(value)}
      </text>

      {/* Unit text */}
      <text x={x} y={y + 12} dx={`${Math.round(value).toString().length * 6.5 + 3}`} fill="#B0B0B0" fontSize={14} fontWeight="600" fontFamily="Inter, sans-serif" textAnchor="start" className="pointer-events-none select-none" data-id="element-375">
        {unit}
      </text>

      {/* Hover hint */}
      {isHovered && <motion.text x={x} y={y + 38} fill="#4FC3F7" fontSize={11} fontWeight="600" fontFamily="Inter, sans-serif" textAnchor="middle" className="pointer-events-none select-none" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} data-id="element-376">
          Click to edit
        </motion.text>}
    </motion.g>;
}